import { BuildPresentationProps } from '@features/lci-report/subdomains/visitation/utils/build-presentation';
import { Slide } from '@features/powerpoint';
import { audienceProfileChartSlide } from './audience-profile/audience-profile-chart-slide';
import { campaignAnalyticsTitleSlide } from './campaign-analytics-title-slide';
import { CampaignAnalyticsReport } from '@components/Dashboard/CampaignAnalytics/slice/schema';
import { demographicProfileChartSlide } from './demographic-profile/demographic-profile-chart-slide';
import { stateAndDmaVisitsTableSlide } from './state-and-dma-visits-slide';
import { brandLocationsTableSlide } from './brand-locations-slide';
import { proximityToHomeChartSlide } from './plain-chart-slides/proximity-to-home-chart-slide';
import { timeToFirstVisitChartSlide } from './plain-chart-slides/time-to-first-visit-chart-slide';
import { visitsByTimeOfDayChartSlide } from './plain-chart-slides/visits-by-time-of-day-chart-slide';
import { visitsByDayOfWeekChartSlide } from './plain-chart-slides/visits-by-day-of-week-chart-slide';
import { footTrafficOverlapChartSlide } from './plain-chart-slides/foot-traffic-overlap-chart-slide';

export type CampaignAnalyticsSlideProps = {
  slideMaster: string;
  campaignAnalyticsReport: CampaignAnalyticsReport;
};

const campaignAnalyticsTitleSlideFromProps = (
  props: BuildPresentationProps
): Slide[] =>
  Object.values(props.customFields.includedSlides.campaignAnalytics).some(
    Boolean
  )
    ? [campaignAnalyticsTitleSlide]
    : [];

const audienceProfileChartSlideFromProps = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] => {
  return props.customFields.includedSlides.campaignAnalytics.audienceProfile &&
    props.campaignAnalyticsReport?.audienceProfile.visible
    ? audienceProfileChartSlide({
        slideMaster,
        campaignAnalyticsReport: props.campaignAnalyticsReport,
      })
    : [];
};

const demographicProfileChartSlideFromProps = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] => {
  return props.customFields.includedSlides.campaignAnalytics
    .demographicProfile &&
    props.campaignAnalyticsReport?.demographicProfile.visible
    ? [
        demographicProfileChartSlide({
          slideMaster,
          campaignAnalyticsReport: props.campaignAnalyticsReport,
        }),
      ]
    : [];
};

const stateAndDmaVisitsTableSlideFromProps = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] => {
  return props.customFields.includedSlides.campaignAnalytics
    .stateAndDmaVisits &&
    (props.campaignAnalyticsReport?.dmaVisits.visible ||
      props.campaignAnalyticsReport?.stateVisits.visible)
    ? [
        stateAndDmaVisitsTableSlide({
          slideMaster,
          campaignAnalyticsReport: props.campaignAnalyticsReport,
        }),
      ]
    : [];
};

const brandLocationsTableSlideSlideFromProps = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] => {
  return props.customFields.includedSlides.campaignAnalytics
    .brandLocationVisits &&
    props.campaignAnalyticsReport?.brandLocationVisits.visible
    ? [
        brandLocationsTableSlide({
          slideMaster,
          campaignAnalyticsReport: props.campaignAnalyticsReport,
        }),
      ]
    : [];
};

const proximityToHomeChartSlideSlideFromProps = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] => {
  return props.customFields.includedSlides.campaignAnalytics.proximityToHome &&
    props.campaignAnalyticsReport?.proximityToHome.visible
    ? [
        proximityToHomeChartSlide({
          slideMaster,
          campaignAnalyticsReport: props.campaignAnalyticsReport,
        }),
      ]
    : [];
};

const timeToFirstVisitChartSlideFromProps = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] => {
  return props.customFields.includedSlides.campaignAnalytics
    .timeToFirstVisitByDay &&
    props.campaignAnalyticsReport?.timeToFirstVisitByDay.visible
    ? [
        timeToFirstVisitChartSlide({
          slideMaster,
          campaignAnalyticsReport: props.campaignAnalyticsReport,
        }),
      ]
    : [];
};

const visitsByTimeOfDayChartSlideFromProps = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] => {
  return props.customFields.includedSlides.campaignAnalytics
    .visitsByHourOfDay &&
    props.campaignAnalyticsReport?.visitsByHourOfDay.visible
    ? [
        visitsByTimeOfDayChartSlide({
          slideMaster,
          campaignAnalyticsReport: props.campaignAnalyticsReport,
        }),
      ]
    : [];
};

const visitsByDayOfWeekChartSlideFromProps = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] => {
  return props.customFields.includedSlides.campaignAnalytics
    .visitsByDayOfWeek &&
    props.campaignAnalyticsReport?.visitsByDayOfWeek.visible
    ? [
        visitsByDayOfWeekChartSlide({
          slideMaster,
          campaignAnalyticsReport: props.campaignAnalyticsReport,
        }),
      ]
    : [];
};

const footTrafficOverlapChartSlideFromProps = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] => {
  return props.customFields.includedSlides.campaignAnalytics
    .footTrafficOverlap &&
    props.campaignAnalyticsReport?.footTrafficOverlap.visible
    ? [
        footTrafficOverlapChartSlide({
          slideMaster,
          campaignAnalyticsReport: props.campaignAnalyticsReport,
        }),
      ]
    : [];
};

export const campaignAnalyticsSlides = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] => {
  const slides = props.campaignAnalyticsReport
    ? [
        campaignAnalyticsTitleSlideFromProps(props),
        audienceProfileChartSlideFromProps(props, slideMaster),
        demographicProfileChartSlideFromProps(props, slideMaster),
        stateAndDmaVisitsTableSlideFromProps(props, slideMaster),
        brandLocationsTableSlideSlideFromProps(props, slideMaster),
        proximityToHomeChartSlideSlideFromProps(props, slideMaster),
        timeToFirstVisitChartSlideFromProps(props, slideMaster),
        visitsByTimeOfDayChartSlideFromProps(props, slideMaster),
        visitsByDayOfWeekChartSlideFromProps(props, slideMaster),
        footTrafficOverlapChartSlideFromProps(props, slideMaster),
      ].flat()
    : [];

  return slides;
};
